/**
 * Clinical-document Postgres write for Final Documents submit and the
 * document merger modal.
 *
 * Calls `POST /medical/frontend-query/upsert/clinical-document`
 * via the generic upsert client. Supports `clinical_attachment` (fax
 * submit) and `submission_doc` (merged PDF from the three-dots merger).
 * @module updateClinicalDocument
 */

import { DocumentPageMapping } from "../../data-model/medicalPaOrdersModel";
import { SingleOrderDetailsTableName } from "../../enums/singleOrderDetailsTableName";
import {
  singleOrderDetailsGenericUpsert,
  SingleOrderDetailsGenericUpsertResponse,
} from "../postgreSQL/postCalls/singleOrderDetailsGenericUpsert";

/**
 * Request body for upserting a clinical document row in Postgres.
 *
 * Matches `OrderUpdateRequest` for `/frontend-query/upsert/clinical-document`.
 */
export interface UpdateClinicalDocumentPayload {
  /** Medical PA order identifier. */
  order_id: string;
  /** Organization / facility ID scoping the write. */
  org_id: string;
  /** Clinical document fields persisted on the `clinical_document` table. */
  data: {
    /** Document type discriminator for the `clinical_document` row. */
    document_type: "clinical_attachment" | "submission_doc";
    /** Patient identifier from order demographics. */
    patient_id: string;
    /** Firebase Storage path of the uploaded clinical PDF. */
    file_path: string;
    /** Display / storage file name (e.g. `{uuid}.pdf`). */
    document_name: string;
    /** Full merged-PDF page mapping; sent when the user added documents. */
    document_page_mapping?: DocumentPageMapping[];
  };
}

/**
 * Upserts a clinical-document row keyed by `(order_id, document_type)`.
 *
 * @param payload - Order-scoped clinical document create/update body
 * @returns Upsert result from the frontend-query upsert endpoint
 * @throws {Error} When the request fails or returns a non-OK status
 */
export const updateClinicalDocument = async (
  payload: UpdateClinicalDocumentPayload,
): Promise<SingleOrderDetailsGenericUpsertResponse> => {
  return singleOrderDetailsGenericUpsert({
    order_id: payload.order_id,
    org_id: payload.org_id,
    table_name: SingleOrderDetailsTableName.CLINICAL_DOCUMENT,
    data: payload.data,
  });
};
